import { SplitFieldRequestBody, SplitFieldResponse, axios } from "utils";

import { PUBLIC_FIELDS_URL as endpoint } from "consts";

import {
  AddFieldRequestBody,
  BasicFieldDetails,
  BulkCropPracticesRequest,
  BulkCropPracticesResponse,
  CropPracticesRequestBody,
  CropPracticesResponse,
  EVALUATION_METRICS,
  FieldCropRecommendationsResponse,
  FieldCropRecommendationsSimulationsResponse,
  FieldDetails,
  FieldEvaluationMetricCorrectionsResponse,
  FieldListField,
  FieldPracticeFilterOptionsResponse,
  FieldPracticeResponse,
  FieldSimulation,
  FieldsListResponse,
} from "models";

export const getFields = async (data: { signal: AbortSignal }): Promise<FieldsListResponse> => axios.get(`${endpoint}`, { signal: data.signal });

export const getHaInConversion = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/ha-in-conversion`, { data });

export const getFieldCount = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/count`, { data });

export const getRedzoneFieldCount = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/redzone-field-count`, { data });

export const getFieldBasicDetails = async (fieldId: string, includeDisabledFields?: boolean): Promise<BasicFieldDetails> =>
  axios.get(`${endpoint}/${fieldId}/basic-details${includeDisabledFields ? "?includeDisabledFields=true" : ""}`);

export const getField = async (data: { id: string; includeDisabledFields?: boolean; signal: AbortSignal }): Promise<FieldDetails> =>
  axios.get(`${endpoint}/${data.id}${data.includeDisabledFields ? "?includeDisabledFields=true" : ""}`, { signal: data.signal });

export const uploadFields = async (data: { file: string }) => axios.post(`${endpoint}/upload`, { data });

export const uploadFieldsExperiment = async (data: { file: string }) => axios.post(`${endpoint}/upload-experiment`, { data });

export const addField = async ({ signal, ...data }: { signal: AbortSignal } & AddFieldRequestBody) => axios.post(`${endpoint}`, data, { signal });

export const updateField = async (data: { id: string; signal: AbortSignal }) => axios.put(`${endpoint}/${data.id}`, { data, signal: data.signal });

export const deleteMultipleFields = async (data: { fields: string[] }) => axios.delete(`${endpoint}`, { data });

export const deleteField = async (data: { fieldId: string; signal?: AbortSignal }) =>
  axios.delete(`${endpoint}/${data.fieldId}`, { data, signal: data.signal });

export const splitField = async (data: SplitFieldRequestBody): Promise<SplitFieldResponse> => axios.post(`${endpoint}/${data.fieldId}/split`, { data });

export const getFieldListByFarmId = async ({ farmId }: { farmId: string }): Promise<FieldListField[]> => axios.get(`${endpoint}/list?farmId=${farmId}`);

export const getFarmFieldListAtPointInTime = async ({ farmId, date }: { farmId: string; date: string }): Promise<FieldListField[]> =>
  axios.get(`${endpoint}/list?farmId=${farmId}&date=${date}`);

export const addFieldSimulation = async (data: { fieldId: string; name: string; signal?: AbortSignal }): Promise<FieldSimulation[]> =>
  axios.post(`${endpoint}/${data.fieldId}/simulations`, { name: data.name, signal: data?.signal });

export const getFieldSimulations = async (data: { fieldId: string; signal?: AbortSignal }): Promise<FieldSimulation[]> =>
  axios.get(`${endpoint}/${data.fieldId}/simulations`, { signal: data?.signal });

export const getFieldPractices = async (data: { fieldId: string }): Promise<FieldPracticeResponse> => axios.get(`${endpoint}/${data.fieldId}/practices`);

export const getFieldPracticesSimulations = async (data: { fieldId: string; simulationId: string }): Promise<FieldPracticeResponse> =>
  axios.get(`${endpoint}/${data.fieldId}/simulations/${data.simulationId}/practices`);

export const getFieldPracticesBulk = async ({ fieldIds, signal }: { fieldIds: string[]; signal: AbortSignal }): Promise<FieldPracticeResponse[]> =>
  axios.post(`${endpoint}/practices`, { data: { fieldIds }, signal });

export const addFieldCropPractice = async ({
  fieldId,
  requestBody,
}: {
  fieldId: string;
  requestBody: CropPracticesRequestBody;
}): Promise<CropPracticesResponse> => axios.post(`${endpoint}/${fieldId}/crops`, { data: requestBody });

export const addFieldCropPracticeSimulations = async ({
  fieldId,
  simulationId,
  requestBody,
}: {
  fieldId: string;
  simulationId: string;
  requestBody: CropPracticesRequestBody;
}): Promise<CropPracticesResponse> => axios.post(`${endpoint}/${fieldId}/simulations/${simulationId}/crops`, { data: requestBody });

export const updateFieldCropPractice = async ({
  fieldId,
  cropId,
  requestBody,
}: {
  fieldId: string;
  cropId: string;
  requestBody: CropPracticesRequestBody;
}): Promise<CropPracticesResponse> => axios.put(`${endpoint}/${fieldId}/crops/${cropId}`, { data: requestBody });

export const updateFieldCropPracticeSimulations = async ({
  fieldId,
  cropId,
  simulationId,
  requestBody,
}: {
  fieldId: string;
  cropId: string;
  simulationId: string;
  requestBody: CropPracticesRequestBody;
}): Promise<CropPracticesResponse> => axios.put(`${endpoint}/${fieldId}/simulations/${simulationId}/crops/${cropId}`, { data: requestBody });

export const deleteFieldCropPractice = async ({ fieldId, cropId }: { fieldId: string; cropId: string }): Promise<CropPracticesResponse> =>
  axios.delete(`${endpoint}/${fieldId}/crops/${cropId}`);

export const deleteFieldCropPracticeSimulations = async ({
  fieldId,
  simulationId,
  cropId,
}: {
  fieldId: string;
  simulationId: string;
  cropId: string;
}): Promise<CropPracticesResponse> => axios.delete(`${endpoint}/${fieldId}/simulations/${simulationId}/crops/${cropId}`);

export const getFieldCropRecommendations = async (data: { fieldIds: string[] }): Promise<FieldCropRecommendationsResponse> =>
  axios.post(`${endpoint}/crop-recommendations`, { fieldIds: data.fieldIds });

export const getFieldCropRecommendationsSimulations = async (data: {
  simulationIds: string[];
  signal: AbortSignal;
}): Promise<FieldCropRecommendationsSimulationsResponse> =>
  axios.post(`${endpoint}/crop-recommendations-simulations`, { simulationIds: data.simulationIds }, { signal: data.signal });

export const addFieldPracticeParticipations = async (data: { fieldId: string; practiceSlugs: string[] }) =>
  axios.post(`${endpoint}/practice-participations`, data);

export const deleteFieldPracticeParticipations = async (data: { fieldId: string; practiceSlugs: string[] }) =>
  axios.delete(`${endpoint}/practice-participations`, { data: data });

export const getFieldEvaluationMetricCorrections = async (
  data: { fieldIds: string[] },
  signal: AbortSignal
): Promise<FieldEvaluationMetricCorrectionsResponse> => axios.post(`${endpoint}/evaluation-metric-corrections`, { fieldIds: data.fieldIds }, { signal });

export const addOrUpdateFieldEvaluationMetricCorrection = async ({
  fieldId,
  data,
  correctionId,
}: {
  fieldId: string;
  data: { year: number; value: number; metric: EVALUATION_METRICS };
  correctionId?: string;
}): Promise<{ id: string }> => {
  if (correctionId) {
    return axios.put(`${endpoint}/${fieldId}/evaluation-metric-corrections/${correctionId}`, data);
  }

  return axios.post(`${endpoint}/${fieldId}/evaluation-metric-corrections`, data);
};

export const deleteFieldEvaluationMetricCorrection = async (data: { fieldId: string; correctionId: string }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/${data.fieldId}/evaluation-metric-corrections/${data.correctionId}`);

export const updateBulkFieldCropPractice = async ({ data }: { data: BulkCropPracticesRequest[] }): Promise<BulkCropPracticesResponse> =>
  axios.put(`${endpoint}/crops/bulk`, { data });

export const getFieldPracticeFilterOptions = async (): Promise<FieldPracticeFilterOptionsResponse> => axios.get(`${endpoint}/practice-filter-options`);

export const getPracticeFilteredFieldIds = async (data: { cropIds?: string[]; years?: number[] }): Promise<{ fieldIds: string[] }> =>
  axios.post(`${endpoint}/practice-filtered-fields`, {
    cropIds: data.cropIds || [],
    years: data.years || [],
  });
